
import { Link } from 'react-router-dom';
import { useReviews } from '../../../hooks/useReviews';

const avatarColors = [
  'bg-teal-100 text-teal-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-emerald-100 text-emerald-700',
  'bg-orange-100 text-orange-700',
  'bg-cyan-100 text-cyan-700',
];

export default function CustomerWins() {
  const { reviews, loading } = useReviews();
  const featured = reviews.filter((r) => r.rating >= 4).slice(0, 6);

  const getInitials = (name: string) => {
    if (!name) return 'A';
    return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <section className="py-20 px-4 sm:px-6 bg-stone-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <span className="inline-block text-teal-600 text-xs font-semibold tracking-wider uppercase mb-3">
            Real Stories, Real Savings
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            <a href="#wins" className="hover:text-teal-600 transition-colors">
              Customers Who Chose Rockhound Auto
            </a>
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto">
            Hear from Pacific Northwest residents who chose Rockhound Auto for honest pricing and transparent service.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full" />
                  <div className="space-y-1">
                    <div className="h-3 bg-gray-200 rounded w-24" />
                    <div className="h-3 bg-gray-100 rounded w-16" />
                  </div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-5/6" />
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 p-12 text-center shadow-sm">
            <i className="ri-star-line text-5xl text-teal-400 mb-4 block"></i>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Reviews Yet</h3>
            <p className="text-gray-600 mb-6">Be the first to share your experience with Rockhound Auto.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/write-a-review"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap"
              >
                <i className="ri-edit-line"></i>
                Write a Review
              </Link>
              <a
                href="https://share.google/vqOn8ooyXd5MkpaOe"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-6 py-3 rounded-full font-semibold hover:border-teal-400 transition-colors whitespace-nowrap"
              >
                <i className="ri-google-fill text-teal-500"></i>
                Review on Google
              </a>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((r, idx) => (
              <div
                key={r.id}
                className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-bold ${avatarColors[idx % avatarColors.length]}`}>
                    {getInitials(r.name)}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{r.name}</p>
                    <p className="text-xs text-gray-500">{r.location}</p>
                  </div>
                </div>
                <div className="flex-1 mb-4">
                  <i className="ri-double-quotes-l text-teal-200 text-2xl leading-none"></i>
                  <p className="text-sm text-gray-600 leading-relaxed mt-1">{r.text}</p>
                </div>
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div>
                    {r.vehicle_purchased && (
                      <p className="text-xs text-gray-500">{r.vehicle_purchased}</p>
                    )}
                    <div className="flex gap-0.5 mt-1">
                      {[...Array(5)].map((_, si) => (
                        <i key={si} className={`text-sm ${si < r.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}></i>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
